import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { push, getParsedQuery } from "yarrr";

import request from "../../Api/request";
import Spacer from "../../Components/Spacer";
import { useEnvironment } from "../../Hooks";
import Button from "../../Components/Button";
import Modal from "../../Components/Modal";
import Tabs from "../../Components/Tabs";
import { getPerformYardUsers } from "../../Api/services/logan-users";
import { LoganUserModal } from "./LoganUserModal";
import DataGrid from "./DataGrid";
import SuperUserModal from "./SuperUserModal";
import { SUPER_USERS_COLUMNS, LOGAN_USERS_COLUMNS } from "./gridConfig";

import css from "./styles.styl";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

const useQueryParams = () => {
	const dispatch = useDispatch();
	const query = useSelector(getParsedQuery);

	const updateQueryParam = (key, value) => {
		const currentParams = new URLSearchParams(window.location.search);
		if (value) {
			currentParams.set(key, value);
		} else {
			currentParams.delete(key);
		}
		const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
		dispatch(push(newUrl));
	};

	return { query, updateQueryParam };
};

const TAB_MAP = {
	"Super Users": "super-users",
	"Logan Users": "logan-users"
};

const REVERSE_TAB_MAP = {
	"super-users": "Super Users",
	"logan-users": "Logan Users"
};


const SuperUsersPage = () => {
	const environment = useEnvironment();
	const queryClient = useQueryClient();
	const [openModal, setOpenModal] = useState(null);
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const { query, updateQueryParam } = useQueryParams();

	const activeTab = REVERSE_TAB_MAP[query.tab] || "Super Users";
	const setActiveTab = (tab) => updateQueryParam("tab", TAB_MAP[tab]);

	const superUsersQuery = useQuery({
		queryKey: ["super-users", environment],
		queryFn: async () => {
			try {
				const response = await request({
					url: `/${environment}/super-users`,
					method: "GET",
				});

				const superUsers = response.data.superUsers.map((user) => {
					return {
						...user,
						lastSeen: user.lastSeen ? new Date(user.lastSeen) : null,
						createdAt: user.createdAt ? new Date(user.createdAt) : null,
						firstLoginDate: user.firstLoginDate ? new Date(user.firstLoginDate) : null,
					};
				});

				return { superUsers };
			} catch (error) {
				console.error(error);
				return [];
			}
		},
	});

	const loganUsersQuery = useQuery({
		queryKey: ["logan-users"],
		queryFn: async () => {
			try {
				const users = await getPerformYardUsers();
				return users || [];
			} catch (error) {
				console.error(error);
				return [];
			}
		},
	});

	const { isFetching } = activeTab === "Super Users" ? superUsersQuery : loganUsersQuery;

	const handleAddSuperUser = async () => {
		await request({
			url: `/${environment}/super-users/add`,
			method: "POST",
			data: { id: selectedEmployee._id },
		});
		queryClient.invalidateQueries({ queryKey: ["super-users", environment] });
		setOpenModal(null);
		setSelectedEmployee(null);
	};

	const handleCloseModal = () => {
		setOpenModal(null);
		setSelectedEmployee(null);
	};

	const currentData = activeTab === "Super Users" 
		? superUsersQuery?.data?.superUsers 
		: loganUsersQuery?.data;
	
	const currentColumns = activeTab === "Super Users" 
		? SUPER_USERS_COLUMNS 
		: LOGAN_USERS_COLUMNS;

	return (
		<div className={css.content}>
			<div className={css.sectionInformation}>
				<div className={css.tabHeader}>
					<Tabs
						tabs={["Super Users", "Logan Users"]}
						currentTab={activeTab}
						onChange={setActiveTab}
					/>
					<div className={css.addUserButtonContainer}>
						<Button
							color="green"
							onClick={() => setOpenModal(activeTab === "Super Users" ? "super-user" : "logan-user")}
						>
							{activeTab === "Super Users" ? "Add Super User" : "Add Logan User"}
						</Button>
					</div>
				</div>

				<Spacer size={2} />

				<LoganUserModal
					isOpen={openModal === "logan-user"}
					onClose={handleCloseModal}
				/>

				{openModal === "super-user" && (
					<Modal
						size="large"
						content={
							<SuperUserModal
								selectedEmployee={selectedEmployee}
								environment={environment}
								onEmployeeSelect={setSelectedEmployee}
								onCancel={handleCloseModal}
								onConfirm={handleAddSuperUser}
							/>
						}
						buttons={[]}
						handleClose={handleCloseModal}
					/>
				)}

				<DataGrid
					data={currentData}
					columns={currentColumns}
					isLoading={isFetching}
				/>
			</div>
		</div>
	);
};

export default SuperUsersPage;
